/**
 * Edit sessions of the storage preview pane, one per tab. A session pins the pane to the object being edited —
 * selection changes do not move it — and holds the baseline (text, ETag, encoding) the save compares against. Living
 * outside the pane lets an edit survive the pane being hidden behind the Properties panel, and lets every close path
 * ask before unsaved edits go (`hasUnsavedStoragePreview`).
 *
 * Unsaved drafts survive quitting the app like query text does: dirty sessions up to `DRAFT_PERSIST_MAX_CHARS` (draft +
 * baseline, all tabs together) are persisted and come back with their restored tab; Save then still checks the ETag
 * they were loaded at. Typing reaches storage on a debounce, flushed by the quit / window-close session save
 * (`pendingBufferFlushes`) like a query tab's buffer. A session ends when its tab leaves the workspace (a subscription — not an unmount, which React's
 * StrictMode also fakes in development).
 */
import { create } from 'zustand';
import { persist, type PersistStorage, type StorageValue } from 'zustand/middleware';
import type { StorageOperationError } from '../../../../types/azureStorage';
import type { StorageContentRef } from '../../../../types/azureStorageContent';
import { createGuardedJsonStorage } from '../../../../core/storage/guardedJsonStorage';
import { showPersistWriteFailureToast } from '../../../../components/ui/persistWriteFailureToast';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { registerPendingBufferFlush } from '../../core/store/pendingBufferFlushes';
import type { PreviewTextEncoding } from './previewText';

export type StoragePreviewBaseline = { text: string; etag: string; encoding: PreviewTextEncoding; bom: boolean; contentType?: string; size: number };

export type StoragePreviewSessionStatus = 'editing' | 'saving' | 'conflict';

export type StoragePreviewSession = {
  /** Unique per edit session: a save's answer applies only to the session that sent it. */
  id: string;
  ref: StorageContentRef;
  /** The object's name as shown (its last path segment). */
  name: string;
  baseline: StoragePreviewBaseline;
  buffer: string;
  status: StoragePreviewSessionStatus;
  /** The last failed save's error (cleared by the next save; by the next edit too unless it is unresolved — `keepsError`). */
  error?: StorageOperationError;
  /** When the buffer last changed (ms): a persisted draft nobody touched for `DRAFT_MAX_AGE_MS` is dropped. */
  editedAt?: number;
  /** Temporary copies saves (or copies) could not remove, as `container/path` (or `share/path`): named apart from
   * `error` until Reload or the session ends. */
  leftovers?: string[];
};

/** What persisted drafts may hold in all (characters of draft + baseline), so drafts never crowd out the session. */
export const DRAFT_PERSIST_MAX_CHARS = 1_000_000;
/** A draft whose tab never came back (a session not resumed) stops holding budget after this long without an edit. */
export const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

type SessionState = {
  byTab: Record<string, StoragePreviewSession | undefined>;
  start: (tabId: string, session: Omit<StoragePreviewSession, 'id' | 'buffer' | 'status'>) => void;
  setBuffer: (tabId: string, buffer: string) => void;
  patch: (tabId: string, patch: Partial<StoragePreviewSession>) => void;
  end: (tabId: string) => void;
};

/** Typing does not settle a conflict (which version, deleted or changed) or a save whose outcome is unknown. */
const keepsError = (session: StoragePreviewSession): boolean => session.status === 'conflict' || session.error?.category === 'ambiguous_outcome';

const newSessionId = (): string => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

export const isStoragePreviewDirty = (session: StoragePreviewSession | undefined): boolean => !!session && session.buffer !== session.baseline.text;

const draftChars = (session: StoragePreviewSession): number => session.buffer.length + session.baseline.text.length;

/** The dirty sessions that fit the persisted budget (smallest first), restored as plain editing sessions. */
export function persistableDrafts(byTab: SessionState['byTab'], now = Date.now()): Record<string, StoragePreviewSession> {
  let used = 0;
  const kept: Record<string, StoragePreviewSession> = {};
  Object.entries(byTab)
    .flatMap(([tabId, session]) => (session && isStoragePreviewDirty(session) && now - (session.editedAt ?? now) <= DRAFT_MAX_AGE_MS ? [[tabId, session] as const] : []))
    .sort(([, a], [, b]) => draftChars(a) - draftChars(b))
    .forEach(([tabId, session]) => {
      if (used + draftChars(session) > DRAFT_PERSIST_MAX_CHARS) return;
      used += draftChars(session);
      kept[tabId] = { id: session.id, ref: session.ref, name: session.name, baseline: session.baseline, buffer: session.buffer, status: 'editing', editedAt: session.editedAt, ...(session.leftovers?.length ? { leftovers: session.leftovers } : {}) };
    });
  return kept;
}

type PersistedDrafts = { byTab: Record<string, StoragePreviewSession> };

/** How long typing waits before the drafts are written (a write serialises every kept draft). */
const DRAFT_PERSIST_DEBOUNCE_MS = 500;

/** `inner`, with writes coalesced on a debounce; the pending write is pushed by `flushPendingBuffers` and on unload. */
function debouncedDraftStorage(inner: PersistStorage<PersistedDrafts> | undefined): PersistStorage<PersistedDrafts> | undefined {
  if (!inner) return inner;
  let pending: { name: string; value: StorageValue<PersistedDrafts> } | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    const next = pending;
    pending = undefined;
    if (next) void inner.setItem(next.name, next.value);
  };
  registerPendingBufferFlush(flush);
  if (typeof window !== 'undefined') window.addEventListener('beforeunload', flush);
  return {
    getItem: (name) => inner.getItem(name),
    setItem: (name, value) => {
      pending = { name, value };
      timer ??= setTimeout(flush, DRAFT_PERSIST_DEBOUNCE_MS);
    },
    removeItem: (name) => {
      pending = undefined;
      return inner.removeItem(name);
    },
  };
}

export const useStoragePreviewSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      byTab: {},
      start: (tabId, session) => set((state) => ({ byTab: { ...state.byTab, [tabId]: { ...session, id: newSessionId(), buffer: session.baseline.text, status: 'editing' } } })),
      setBuffer: (tabId, buffer) => set((state) => {
        const session = state.byTab[tabId];
        if (!session || session.buffer === buffer) return state;
        return { byTab: { ...state.byTab, [tabId]: { ...session, buffer, error: keepsError(session) ? session.error : undefined, editedAt: Date.now() } } };
      }),
      patch: (tabId, patch) => set((state) => {
        const session = state.byTab[tabId];
        return session ? { byTab: { ...state.byTab, [tabId]: { ...session, ...patch } } } : state;
      }),
      end: (tabId) => set((state) => {
        if (!state.byTab[tabId]) return state;
        const byTab = { ...state.byTab };
        delete byTab[tabId];
        return { byTab };
      }),
    }),
    {
      name: 'jam-sql-storage-preview-drafts',
      version: 2,
      // v1 drafts (pre-release builds) had no session id: give each one.
      migrate: (persisted, version) => {
        const byTab = (persisted as { byTab?: Record<string, StoragePreviewSession> } | undefined)?.byTab ?? {};
        return { byTab: version < 2 ? Object.fromEntries(Object.entries(byTab).map(([tabId, draft]) => [tabId, { ...draft, id: draft.id ?? newSessionId() }])) : byTab };
      },
      storage: debouncedDraftStorage(createGuardedJsonStorage<PersistedDrafts>({ onFailure: showPersistWriteFailureToast('your unsaved file edits', { plural: true }) })),
      partialize: (state) => ({ byTab: persistableDrafts(state.byTab) }),
    },
  ),
);

/** The tab has edits nobody saved yet (every close path asks first). */
export function hasUnsavedStoragePreview(tabId: string): boolean {
  return isStoragePreviewDirty(useStoragePreviewSessionStore.getState().byTab[tabId]);
}

/** Whether this session's unsaved draft is kept if the app quits (it fits the persisted budget). */
export function isDraftKeptOnQuit(tabId: string): boolean {
  return tabId in persistableDrafts(useStoragePreviewSessionStore.getState().byTab);
}

// A session goes with its tab: closing it (every path asked first), a session switch, a disconnect. (Guarded: unit
// tests that mock the workspace store hand over a plain object.)
if (typeof useWorkspaceStore.subscribe === 'function') {
  useWorkspaceStore.subscribe((state, previous) => {
    if (state.tabs === previous.tabs) return;
    const open = new Set(state.tabs.map((tab) => tab.id));
    previous.tabs.forEach((tab) => { if (!open.has(tab.id)) useStoragePreviewSessionStore.getState().end(tab.id); });
  });
}
